/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Avatar, Pressable } from '@components/atoms'
import { Menu, MenuItem as MuiMenuItem } from '@mui/material'
import { mono } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppSelector,
	useAppDispatch,
	authActions,
	adminActions,
	routeActions,
	ConfigStateType,
} from '@redux/index'
import { UserType } from '@schemas'
import { useNavigate } from 'react-router-dom'
import { useDrawerOpen } from '@components/systems/backoffice/BoShellContext'

/** 사이드바 바닥의 계정 자리 — 28px 아바타 + 이름. 메뉴는 MUI Menu 그대로(테마가 외형을 정한다) */
export const LogedInUserInfo = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const user: UserType = useAppSelector(state => state.user.user)
	const isOpenDrawer: boolean = useDrawerOpen()
	const isMobile: boolean = useAppSelector(state => state.config.isMobile)

	// state
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

	// variables
	const open = Boolean(anchorEl)

	const onOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const onCloseMenu = React.useCallback(() => {
		setAnchorEl(null)
	}, [])

	const onTurnOffAdminMode = React.useCallback(() => {
		onCloseMenu()
		reduxDispatch(adminActions.TRIGGER_LEAVE_TO_ADMIN(true))
	}, [])

	const onAdminClick = React.useCallback(() => {
		onCloseMenu()
		reduxDispatch(adminActions.TRIGGER_GO_TO_ADMIN(true))
	}, [])

	const onLogoutClick = React.useCallback(() => {
		onCloseMenu()
		reduxDispatch(authActions.TRIGGER_LOGOUT(true))
	}, [])

	const onGoHomeButton = React.useCallback(() => {
		onCloseMenu()
		navigate('/main/home')
	}, [])

	// 본인 정보 수정. 이 앱의 화면 이동은 redux(routeActions.NAVIGATE_PATH)를 거친다 —
	// react-router 의 navigate() 를 직접 부르면 state.route 와 어긋난다.
	const onMyPageClick = React.useCallback(() => {
		onCloseMenu()
		reduxDispatch(
			routeActions.NAVIGATE_PATH(mode === 'admin' ? '/admin/mypage' : '/main/mypage'),
		)
	}, [mode])

	const MenuItem = ({ name, onClick }: { name: string; onClick: () => void }) => {
		return (
			<MuiMenuItem css={{ minWidth: '11rem' }} onClick={onClick}>
				{name}
			</MuiMenuItem>
		)
	}

	return (
		<View css={style} className={isOpenDrawer ? 'open' : 'closed'}>
			<Pressable
				className="userInfoButton"
				id="userInfoButton"
				aria-controls={open ? 'userInfoMenu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={onOpenMenu}
			>
				<Avatar
					className="userInfoAvatar"
					picture={user.profileImageUrl}
					name={user.name}
					color={user.profileColor === '' ? mono.lineSoft : user.profileColor}
					extpandingDisable={true}
					size={28}
				/>
				{isOpenDrawer ? <span className="userName">{user.name}</span> : null}
			</Pressable>

			<Menu
				id="userInfoMenu"
				anchorEl={anchorEl}
				open={open}
				onClose={onCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				MenuListProps={{
					'aria-labelledby': 'userInfoButton',
				}}
			>
				{!isMobile ? (
					<View>
						<MenuItem name="마이페이지" onClick={onMyPageClick} />
						<MenuItem name="로그아웃" onClick={onLogoutClick} />

						<View>
							{mode === 'admin' ? (
								<MenuItem
									name="관리자 설정 나가기"
									onClick={() => onTurnOffAdminMode()}
								/>
							) : (
								<MenuItem name="관리자 설정" onClick={() => onAdminClick()} />
							)}
						</View>
					</View>
				) : (
					<View>
						<MenuItem name="마이페이지" onClick={onMyPageClick} />
						<MenuItem name="홈으로 가기" onClick={onGoHomeButton} />
						<MenuItem name="로그아웃" onClick={onLogoutClick} />
					</View>
				)}
			</Menu>
		</View>
	)
}

const style = css({
	minWidth: 0,
	flex: 1,

	'.userInfoButton': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: 10,
		width: '100%',
		padding: 0,
		borderRadius: 2,
		transition: 'opacity 200ms ease-out',

		'&:hover': { opacity: 0.72 },

		'.userName': {
			fontSize: 13,
			fontWeight: 400,
			color: mono.ink,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
	},

	'&.closed .userInfoButton': { justifyContent: 'center' },
})
